/**
 * Renders a digger's badge certificate to a PNG, client-side, on a canvas.
 *
 * Certificates are centre-branded. Each centre that has one ships a template
 * bundle under `public/certificate/<template>/` (see CERTIFICATE_TEMPLATES in
 * the shared package), split into two static horizontal bands that carry every
 * fixed element of the printed original:
 *
 *   top.png     rows    0..417   header lockup + the word CERTIFICADO
 *   footer.png  rows 1328..1533  funder logo strip + the orange arc
 *
 * Everything between those bands is white in the original, so this module
 * redraws that middle region — title, body paragraph, date, universe tile — at
 * the coordinates measured off the CICF template. See LAYOUT below. The
 * multi-badge variant that prints several badges on one certificate lives in
 * `certificateSummary.ts` and reuses the primitives exported here.
 */
import type { BadgeUniverso, ParticipantBadge } from 'shared';

/** Page geometry, in template pixels (the source raster minus its grey surround). */
export const PAGE_W = 1228;
export const PAGE_H = 1534;

/**
 * Horizontal centre of the *design*, which sits 6px right of the raster's own
 * centre. Measured from three independently-centred elements in the template
 * (the word CERTIFICADO, the date, and the universe tile), so generated text
 * lines up with the baked-in bands rather than with the page edges.
 */
export const CENTER_X = 620;

export const LAYOUT = {
  top: { y: 0, h: 418 },
  footer: { y: 1328, h: 206 },
  /** Badge name, in the template's orange. Baseline measured off `[Título]`. */
  title: { baseline: 494, size: 71, maxSize: 71, minSize: 40, maxWidth: 1000 },
  /**
   * Body paragraph. The template shows three lines on baselines 676/716/756;
   * real content wraps, so the block is centred on the middle baseline and the
   * font shrinks rather than colliding with the date below.
   */
  body: {
    centerBaseline: 716,
    lineHeight: 40,
    size: 32,
    minSize: 22,
    maxWidth: 960,
    maxLines: 5,
    /**
     * The paragraph is centred on `centerBaseline`, but a tall one must not
     * crowd the date below, so its last baseline is clamped here.
     */
    maxLastBaseline: 786,
  },
  /** Assignment date, bold. */
  date: { baseline: 839, size: 32 },
  /** Universe tile — 238x238 in the template, centred on CENTER_X. */
  tile: { x: 501, y: 989, size: 238 },
} as const;

export const COLORS = {
  /** Sampled from the template's CERTIFICADO wordmark and `[Título]`. */
  title: '#E29A50',
  /** Sampled from the template's body copy. */
  body: '#19202D',
} as const;

const FONT = 'Montserrat, system-ui, sans-serif';

/** A run of body text that is either bold or regular. */
export interface Run {
  text: string;
  bold: boolean;
}

/** A wrapped line: the runs on it, plus its measured width. */
export interface Line {
  runs: Run[];
  width: number;
}

export const font = (size: number, bold: boolean): string =>
  `${bold ? 700 : 400} ${size}px ${FONT}`;

/**
 * Formats the assignment date the way the template shows it — pt-PT long form.
 */
export const formatCertificateDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-PT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

/**
 * Builds the certificate's body sentence as bold/regular runs.
 *
 * The template reads "participou na atividade [TÌTULO]+ [DESCRIÇÃO]." — the `+`
 * is a placeholder-authoring convention meaning "title followed by
 * description", not literal text, so it is rendered as an em dash. When the
 * badge carries no description the clause collapses to just the title.
 */
export const buildBodyRuns = (
  participantName: string,
  badgeName: string,
  badgeDescription: string | null | undefined,
  labels: readonly string[],
): Run[] => {
  const runs: Run[] = [
    { text: 'Certifica-se que ', bold: false },
    { text: participantName, bold: true },
    { text: ' participou na atividade ', bold: false },
  ];

  const description = badgeDescription?.trim();
  runs.push({
    text: description ? `${badgeName} — ${description}.` : `${badgeName}.`,
    bold: true,
  });

  const skills = distinctLabels(labels);
  if (skills.length > 0) {
    runs.push({
      text: ` Trabalhou exemplarmente as seguintes competências: ${skills.join(', ')}.`,
      bold: false,
    });
  }
  return runs;
};

/** What tells two spellings of a label apart is not an accent, a capital or a space. */
const labelKey = (label: string): string =>
  label
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase('pt-PT');

const accents = (label: string): number =>
  label.normalize('NFD').length - label.normalize('NFC').length;

/**
 * The labels as the certificate lists them: each competência once. Two labels
 * are the same when they differ only in capitals, accents, spacing or a full
 * stop at the end, and a label that is itself a list ("rádio, comunicação")
 * counts as the labels in it. The first spelling met is the one kept, unless
 * a later one carries the accents it lacks: "programação" over "programacao".
 */
export const distinctLabels = (labels: readonly string[]): string[] => {
  const kept = new Map<string, string>();
  for (const raw of labels) {
    for (const piece of raw.split(/[,;]/)) {
      const label = piece.replace(/\s+/g, ' ').trim().replace(/[.\s]+$/, '');
      if (label === '') continue;
      const key = labelKey(label);
      const seen = kept.get(key);
      // Setting a key already there keeps its place in the order.
      if (seen === undefined || accents(label) > accents(seen)) kept.set(key, label);
    }
  }
  return [...kept.values()];
};

/** Splits runs into whitespace-delimited words, each keeping its weight. */
const toWords = (runs: readonly Run[]): Run[] => {
  const words: Run[] = [];
  for (const run of runs) {
    // Keep the separating spaces attached to the preceding word so that a line
    // break never swallows the space between two differently-weighted runs.
    for (const piece of run.text.split(/(\s+)/)) {
      if (piece === '') continue;
      words.push({ text: piece, bold: run.bold });
    }
  }
  return words;
};

/** Greedy word-wrap of weighted runs into lines no wider than `maxWidth`. */
export const wrapRuns = (
  ctx: CanvasRenderingContext2D,
  runs: readonly Run[],
  size: number,
  maxWidth: number,
): Line[] => {
  const words = toWords(runs);
  const lines: Line[] = [];
  let current: Run[] = [];
  let width = 0;

  const isSpace = (s: string): boolean => /^\s+$/.test(s);

  for (const word of words) {
    ctx.font = font(size, word.bold);
    const w = ctx.measureText(word.text).width;

    // A space that would land past the edge is dropped rather than wrapped.
    if (isSpace(word.text) && current.length === 0) continue;

    if (width + w > maxWidth && current.length > 0) {
      // Trim a trailing space before committing the line.
      while (current.length > 0 && isSpace(current[current.length - 1].text)) {
        const last = current[current.length - 1];
        ctx.font = font(size, last.bold);
        width -= ctx.measureText(last.text).width;
        current = current.slice(0, -1);
      }
      lines.push({ runs: current, width });
      current = isSpace(word.text) ? [] : [word];
      width = isSpace(word.text) ? 0 : w;
      continue;
    }
    current = [...current, word];
    width += w;
  }
  if (current.length > 0) lines.push({ runs: current, width });
  return lines;
};

/**
 * Wraps `runs` at `size`, shrinking the font one pixel at a time until the
 * paragraph fits in `maxLines` or reaches `minSize`.
 */
export const wrapToFit = (
  ctx: CanvasRenderingContext2D,
  runs: readonly Run[],
  opts: { size: number; minSize: number; maxWidth: number; maxLines: number },
): { lines: Line[]; size: number } => {
  let size = opts.size;
  let lines = wrapRuns(ctx, runs, size, opts.maxWidth);
  while (lines.length > opts.maxLines && size > opts.minSize) {
    size -= 1;
    lines = wrapRuns(ctx, runs, size, opts.maxWidth);
  }
  return { lines, size };
};

/** Draws pre-wrapped lines centred on `CENTER_X`. */
export const drawLines = (
  ctx: CanvasRenderingContext2D,
  lines: readonly Line[],
  size: number,
  firstBaseline: number,
  lineHeight: number,
): void => {
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  lines.forEach((line, i) => {
    let x = CENTER_X - line.width / 2;
    const y = firstBaseline + i * lineHeight;
    for (const run of line.runs) {
      ctx.font = font(size, run.bold);
      ctx.fillText(run.text, x, y);
      x += ctx.measureText(run.text).width;
    }
  });
};

/**
 * Draws a single bold line centred on `CENTER_X` in the template orange,
 * shrinking the font until it fits `LAYOUT.title.maxWidth`.
 */
export const drawTitle = (ctx: CanvasRenderingContext2D, text: string): void => {
  ctx.fillStyle = COLORS.title;
  let size = LAYOUT.title.maxSize;
  ctx.font = font(size, true);
  while (size > LAYOUT.title.minSize && ctx.measureText(text).width > LAYOUT.title.maxWidth) {
    size -= 1;
    ctx.font = font(size, true);
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(text, CENTER_X, LAYOUT.title.baseline, LAYOUT.title.maxWidth);
};

/** Loads an image from the PWA's public assets. */
export const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Não foi possível carregar ${src}`));
    img.src = src;
  });

/**
 * Ensures Montserrat is available to the canvas before any text is measured.
 * Canvas silently falls back to a default face otherwise, which would both
 * mis-measure the wrap and render the wrong typeface.
 */
export const ensureFonts = async (sizes: readonly number[]): Promise<void> => {
  if (typeof document === 'undefined' || !document.fonts) return;
  await Promise.all(
    sizes.flatMap((s) => [
      document.fonts.load(font(s, true)),
      document.fonts.load(font(s, false)),
    ]),
  );
  await document.fonts.ready;
};

/** Asset path for a template's header or footer band. */
export const templateBandSrc = (template: string, band: 'top' | 'footer'): string =>
  `/certificate/${template}/${band}.png`;

/** Loads both bands of a template. */
export const loadTemplateBands = (
  template: string,
): Promise<[HTMLImageElement, HTMLImageElement]> =>
  Promise.all([
    loadImage(templateBandSrc(template, 'top')),
    loadImage(templateBandSrc(template, 'footer')),
  ]);

/** Asset path for a universe tile, or null when the badge has no tile. */
export const universoTileSrc = (universo: BadgeUniverso | null | undefined): string | null => {
  if (universo == null) return null;
  // `Link` badges carry no certificate at all, so they ship no tile.
  if (universo === 'Link') return null;
  return `/certificate/universo/${universo.toLowerCase()}.png`;
};

/** Opens a white page-sized canvas with the template bands painted on it. */
export const openPage = (
  bands: readonly [HTMLImageElement, HTMLImageElement],
  height: number = PAGE_H,
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('O navegador não suporta a criação do certificado.');
  canvas.width = PAGE_W;
  canvas.height = height;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bands[0], 0, LAYOUT.top.y, PAGE_W, LAYOUT.top.h);
  ctx.drawImage(bands[1], 0, height - LAYOUT.footer.h, PAGE_W, LAYOUT.footer.h);
  return { canvas, ctx };
};

/** Encodes a finished page as a PNG blob. */
export const canvasToPng = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error('Não foi possível gerar o certificado.')),
      'image/png',
    );
  });

/**
 * Renders the certificate for one badge assignment on the given centre
 * template and returns it as a PNG blob.
 */
export const renderCertificate = async (
  badge: ParticipantBadge,
  participantName: string,
  template: string,
): Promise<Blob> => {
  const tileSrc = universoTileSrc(badge.badgeUniverso);
  const [bands, tile] = await Promise.all([
    loadTemplateBands(template),
    tileSrc ? loadImage(tileSrc) : Promise.resolve(null),
  ]);
  await ensureFonts([LAYOUT.title.size, LAYOUT.body.size, LAYOUT.date.size]);

  // An unclassified badge has no tile, so the page closes up by exactly the
  // tile's height rather than leaving a void above the footer.
  const collapse = tile ? 0 : LAYOUT.tile.size;
  const { canvas, ctx } = openPage(bands, PAGE_H - collapse);

  // ── Title ──────────────────────────────────────────────────────────────
  drawTitle(ctx, badge.badgeName);

  // ── Body ───────────────────────────────────────────────────────────────
  const runs = buildBodyRuns(
    participantName.trim(),
    badge.badgeName,
    badge.badgeDescription,
    badge.badgeLabels ?? [],
  );
  const { lines, size: bodySize } = wrapToFit(ctx, runs, LAYOUT.body);

  ctx.fillStyle = COLORS.body;
  const span = (lines.length - 1) * LAYOUT.body.lineHeight;
  const firstBaseline = Math.min(
    LAYOUT.body.centerBaseline - span / 2,
    LAYOUT.body.maxLastBaseline - span,
  );
  drawLines(ctx, lines, bodySize, firstBaseline, LAYOUT.body.lineHeight);

  // ── Date ───────────────────────────────────────────────────────────────
  ctx.font = font(LAYOUT.date.size, true);
  ctx.textAlign = 'center';
  ctx.fillText(formatCertificateDate(badge.assignedAt), CENTER_X, LAYOUT.date.baseline);

  // ── Universe tile ──────────────────────────────────────────────────────
  // Unclassified badges simply render without it; the surrounding white closes up.
  if (tile) {
    ctx.drawImage(tile, LAYOUT.tile.x, LAYOUT.tile.y, LAYOUT.tile.size, LAYOUT.tile.size);
  }

  return canvasToPng(canvas);
};

/** Strips characters that are illegal in filenames. */
export const safeFilenamePart = (s: string): string => s.replace(/[\\/:*?"<>|]/g, '').trim();

/** Builds the download filename, e.g. `Certificado - Soldadura - Ana Silva.png`. */
export const certificateFilename = (
  badge: ParticipantBadge,
  participantName: string,
): string =>
  `Certificado - ${safeFilenamePart(badge.badgeName)} - ${safeFilenamePart(participantName)}.png`;

/** Hands a blob to the browser as a download. */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick so the click has committed to the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/** Renders the certificate and hands it to the browser as a download. */
export const downloadCertificate = async (
  badge: ParticipantBadge,
  participantName: string,
  template: string,
): Promise<void> => {
  const blob = await renderCertificate(badge, participantName, template);
  downloadBlob(blob, certificateFilename(badge, participantName));
};
